<template>
	<div class="stepLayout">
		<header class="stepLayout__header">
			<h2 class="stepLayout__title">{{ $t('setupWizard_layout_title') }}</h2>
			<p class="stepLayout__caption">{{ $t('setupWizard_layout_caption') }}</p>
		</header>

		<section class="stepLayout__section">
			<div class="stepLayout__sectionLabel">{{ $t('settings_theme_app_font_size_label') }}</div>
			<div class="stepLayout__fontControls">
				<Slider
					:max="appFontSizePtMax"
					:min="appFontSizePtMin"
					:model-value="store.appFontSizePt"
					:step="appFontSizePtStep"
					@update:model-value="onFontSizeChange"
					class="stepLayout__fontSlider"
				/>
				<div class="stepLayout__fontValue">{{ store.appFontSizePt }}pt</div>
			</div>
		</section>

		<div class="stepLayout__grid">
			<section class="stepLayout__section">
				<div class="stepLayout__sectionLabel">{{ $t('settings_theme_icon_style_title') }}</div>
				<div
					class="stepLayout__tilePair"
					role="radiogroup"
				>
					<button
						:aria-checked="store.menuItemsSolid === true"
						:class="['stepLayout__tile', { 'stepLayout__tile--active': store.menuItemsSolid === true }]"
						@click="onIconStyleChange(true)"
						role="radio"
						type="button"
					>
						<i class="fa-solid fa-gear stepLayout__previewIcon" />
						<span class="stepLayout__tileLabel">{{ $t('settings_theme_icon_style_solid_label') }}</span>
					</button>
					<button
						:aria-checked="store.menuItemsSolid === false"
						:class="['stepLayout__tile', { 'stepLayout__tile--active': store.menuItemsSolid === false }]"
						@click="onIconStyleChange(false)"
						role="radio"
						type="button"
					>
						<i class="fa-regular fa-gear stepLayout__previewIcon" />
						<span class="stepLayout__tileLabel">{{ $t('settings_theme_icon_style_regular_label') }}</span>
					</button>
				</div>
			</section>

			<section class="stepLayout__section">
				<div class="stepLayout__sectionLabel">{{ $t('settings_theme_button_density_title') }}</div>
				<div
					class="stepLayout__tilePair"
					role="radiogroup"
				>
					<button
						:aria-checked="store.shouldUseTheCompactButtonView === false"
						:class="[
							'stepLayout__tile',
							{ 'stepLayout__tile--active': store.shouldUseTheCompactButtonView === false },
						]"
						@click="onButtonViewChange(false)"
						role="radio"
						type="button"
					>
						<span class="stepLayout__previewBtn stepLayout__previewBtn--compact">
							<i :class="iconClass('floppy-disk')" />
						</span>
						<span class="stepLayout__tileLabel">
							{{ $t('settings_theme_button_density_compact_label') }}
						</span>
					</button>
					<button
						:aria-checked="store.shouldUseTheCompactButtonView === true"
						:class="[
							'stepLayout__tile',
							{ 'stepLayout__tile--active': store.shouldUseTheCompactButtonView === true },
						]"
						@click="onButtonViewChange(true)"
						role="radio"
						type="button"
					>
						<span class="stepLayout__previewBtn">
							<i :class="iconClass('floppy-disk')" />
							<span>{{ $t('common_save') }}</span>
						</span>
						<span class="stepLayout__tileLabel">{{ $t('settings_theme_button_density_full_label') }}</span>
					</button>
				</div>
			</section>

			<section class="stepLayout__section">
				<div class="stepLayout__sectionLabel">{{ $t('settings_theme_button_color_mode_title') }}</div>
				<div
					class="stepLayout__tilePair"
					role="radiogroup"
				>
					<button
						:aria-checked="store.shouldUseUniColorButtons === true"
						:class="[
							'stepLayout__tile',
							{ 'stepLayout__tile--active': store.shouldUseUniColorButtons === true },
						]"
						@click="onButtonColorsChange(true)"
						role="radio"
						type="button"
					>
						<span class="stepLayout__previewBtn">
							<i :class="iconClass('floppy-disk')" />
						</span>
						<span class="stepLayout__tileLabel">
							{{ $t('settings_theme_button_color_mode_uni_label') }}
						</span>
					</button>
					<button
						:aria-checked="store.shouldUseUniColorButtons === false"
						:class="[
							'stepLayout__tile',
							{ 'stepLayout__tile--active': store.shouldUseUniColorButtons === false },
						]"
						@click="onButtonColorsChange(false)"
						role="radio"
						type="button"
					>
						<span class="stepLayout__previewBtn stepLayout__previewBtn--multi">
							<i :class="iconClass('floppy-disk')" />
						</span>
						<span class="stepLayout__tileLabel">
							{{ $t('settings_theme_button_color_mode_multi_label') }}
						</span>
					</button>
				</div>
			</section>

			<section class="stepLayout__section">
				<div class="stepLayout__sectionLabel">{{ $t('settings_layout_navigation_mode_label') }}</div>
				<div
					class="stepLayout__tilePair"
					role="radiogroup"
				>
					<button
						:aria-checked="store.navigationMode === 'sidebar'"
						:class="[
							'stepLayout__tile',
							{ 'stepLayout__tile--active': store.navigationMode === 'sidebar' },
						]"
						@click="onNavigationChange('sidebar')"
						role="radio"
						type="button"
					>
						<i
							:class="iconClass('sidebar')"
							class="stepLayout__previewIcon"
						/>
						<span class="stepLayout__tileLabel">{{ $t('settings_layout_navigation_mode_sidebar') }}</span>
					</button>
					<button
						:aria-checked="store.navigationMode === 'topbar'"
						:class="['stepLayout__tile', { 'stepLayout__tile--active': store.navigationMode === 'topbar' }]"
						@click="onNavigationChange('topbar')"
						role="radio"
						type="button"
					>
						<i
							:class="iconClass('window-maximize')"
							class="stepLayout__previewIcon"
						/>
						<span class="stepLayout__tileLabel">{{ $t('settings_layout_navigation_mode_topbar') }}</span>
					</button>
				</div>
			</section>
		</div>
	</div>
</template>

<script lang="ts">
import { computed, defineComponent, inject, onBeforeUnmount } from 'vue';

import Slider from 'primevue/slider';
import { useUserInterfaceSettingsStore } from 'src/stores/userInterfaceSettings';
import {
	APP_FONT_SIZE_PT_STEP,
	MAX_APP_FONT_SIZE_PT,
	MIN_APP_FONT_SIZE_PT,
} from 'src/stores/userInterfaceSettings/appFontSize';
import { type NavigationMode } from 'src/stores/userInterfaceSettings/state';

import { CommandBus } from 'src/util/command/CommandBus';

export default defineComponent({
	components: { Slider },
	name: 'StepButtonsAndLayout',
	setup() {
		const store = useUserInterfaceSettingsStore();
		const commandBus = inject<CommandBus>('commandBus');

		const appFontSizePtMin = MIN_APP_FONT_SIZE_PT;
		const appFontSizePtMax = MAX_APP_FONT_SIZE_PT;
		const appFontSizePtStep = APP_FONT_SIZE_PT_STEP;

		const useSolid = computed<boolean>(() => store.menuItemsSolid);

		function iconClass(name: string): string {
			return useSolid.value ? `fa-solid fa-${name}` : `fa-regular fa-${name}`;
		}

		function onIconStyleChange(solid: boolean): void {
			store.setMenuItemsSolid(solid, commandBus);
		}

		function onButtonViewChange(compact: boolean): void {
			store.setCompactButtonView(compact, commandBus);
		}

		function onButtonColorsChange(uni: boolean): void {
			store.setShouldUseUniColorButtons(uni, commandBus);
		}

		function onNavigationChange(value: NavigationMode): void {
			store.setNavigationMode({ commandBus, value });
		}

		const FONT_SIZE_SYNC_DEBOUNCE_MS = 600;
		let fontSizeSyncTimer: ReturnType<typeof setTimeout> | null = null;

		function onFontSizeChange(value: number | number[]): void {
			const next = Array.isArray(value) ? value[0] : value;
			if (typeof next !== 'number') {
				return;
			}

			store.setAppFontSizePt(next);

			if (fontSizeSyncTimer !== null) {
				clearTimeout(fontSizeSyncTimer);
			}
			fontSizeSyncTimer = setTimeout(() => {
				store.setAppFontSizePt(next, commandBus);
				fontSizeSyncTimer = null;
			}, FONT_SIZE_SYNC_DEBOUNCE_MS);
		}

		onBeforeUnmount(() => {
			if (fontSizeSyncTimer !== null) {
				clearTimeout(fontSizeSyncTimer);
			}
		});

		return {
			appFontSizePtMax,
			appFontSizePtMin,
			appFontSizePtStep,
			iconClass,
			onButtonColorsChange,
			onButtonViewChange,
			onFontSizeChange,
			onIconStyleChange,
			onNavigationChange,
			store,
		};
	},
});
</script>

<style lang="scss" scoped>
.stepLayout {
	display: flex;
	flex-direction: column;
	gap: 1rem;
}

.stepLayout__header {
	margin-bottom: 0;
}

.stepLayout__title {
	font-size: var(--text-xl);
	font-weight: 600;
	letter-spacing: -0.01em;
	margin: 0 0 0.125rem;
}

.stepLayout__caption {
	color: var(--p-text-muted-color);
	font-size: var(--text-sm);
	margin: 0;
}

.stepLayout__grid {
	display: grid;
	gap: 1rem;
	grid-auto-rows: 1fr;
	grid-template-columns: repeat(2, minmax(0, 1fr));
}

.stepLayout__section {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.stepLayout__sectionLabel {
	align-items: flex-start;
	color: var(--p-text-color);
	display: flex;
	font-size: var(--text-xs);
	font-weight: 600;
	letter-spacing: 0.04em;
	line-height: 1.25;
	min-height: 2.4em;
	text-transform: uppercase;
}

.stepLayout__tilePair {
	display: grid;
	flex: 1;
	gap: 0.5rem;
	grid-template-columns: repeat(2, minmax(0, 1fr));
}

.stepLayout__tile {
	align-items: center;
	background: var(--p-surface-0);
	border: 2px solid var(--p-surface-200);
	border-radius: 0.5rem;
	color: var(--p-text-color);
	cursor: pointer;
	display: flex;
	flex-direction: column;
	font-size: var(--text-xs-plus);
	gap: 0.4rem;
	justify-content: center;
	min-height: 4rem;
	padding: 0.625rem 0.5rem;
	transition:
		background-color 150ms ease,
		border-color 150ms ease;

	&:hover:not(.stepLayout__tile--active) {
		background: var(--hover-light);
	}
}

.stepLayout__tile--active {
	background: color-mix(in srgb, var(--p-primary-500) 8%, transparent);
	border-color: var(--p-primary-500);
}

.stepLayout__previewIcon {
	color: var(--p-text-color);
	font-size: var(--text-2xl);
	line-height: 1;
}

.stepLayout__previewBtn {
	align-items: center;
	background: var(--p-surface-100);
	border: 1px solid var(--p-surface-200);
	border-radius: 0.375rem;
	color: var(--p-text-muted-color);
	display: inline-flex;
	font-size: var(--text-xs);
	gap: 0.4rem;
	height: 1.75rem;
	justify-content: center;
	line-height: 1;
	padding: 0 0.625rem;
}

.stepLayout__previewBtn--compact {
	padding: 0;
	width: 1.75rem;
}

.stepLayout__previewBtn--multi {
	background: #ef4444;
	border-color: #ef4444;
	color: #ffffff;
}

.stepLayout__tileLabel {
	color: var(--p-text-color);
	font-weight: 500;
}

.stepLayout__fontControls {
	align-items: center;
	display: flex;
	gap: 1rem;
}

.stepLayout__fontSlider {
	flex: 1;
	min-width: 0;
}

.stepLayout__fontValue {
	color: var(--p-text-muted-color);
	font-size: var(--text-sm);
	font-variant-numeric: tabular-nums;
	min-width: 3rem;
	text-align: right;
}
</style>

<style lang="scss">
.body--dark .stepLayout__tile {
	background: var(--p-surface-800);
	border-color: var(--p-surface-700);
}

.body--dark .stepLayout__tile:hover:not(.stepLayout__tile--active) {
	background: var(--hover-dark);
}

.body--dark .stepLayout__tile--active {
	background: color-mix(in srgb, var(--p-primary-400) 12%, transparent);
	border-color: var(--p-primary-400);
}

.body--dark .stepLayout__previewBtn {
	background: var(--p-surface-700);
	border-color: var(--p-surface-600);
	color: var(--p-surface-200);
}

.body--dark .stepLayout__previewBtn--multi {
	background: #ef4444;
	border-color: #ef4444;
	color: #ffffff;
}
</style>
